/* Shared design tokens and layout for the Services, offer and Privacy pages.
   Matches the homepage (App.js) palette and type. */

export const integrityStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400;1,700&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;1,9..40,300&display=swap');

  :root {
    --ink: #0f0e0d;
    --ink-muted: #5a5650;
    --ink-faint: #b8b2aa;
    --paper: #faf8f4;
    --paper-warm: #f2ede4;
    --accent: #c8392b;
    --accent-light: #f5e8e6;
    --rule: #ddd8cf;
    --serif: 'Playfair Display', Georgia, serif;
    --sans: 'DM Sans', system-ui, sans-serif;
  }

  .di-root * { box-sizing: border-box; margin: 0; padding: 0; }
  .di-root { font-family: var(--sans); background: var(--paper); color: var(--ink); min-height: 100vh; }
  .di-wrap { max-width: 1200px; margin: 0 auto; padding: 0 32px; }
  .di-root a:not([class]) { color: var(--accent); }

  .di-kicker { display: flex; align-items: center; gap: 12px; margin-bottom: 20px; }
  .di-kicker-line { flex: 0 0 32px; height: 2px; background: var(--accent); }
  .di-kicker-text { font-size: 11px; font-weight: 500; letter-spacing: 0.14em; text-transform: uppercase; color: var(--accent); }
  .di-kicker-text a { color: inherit; text-decoration: none; }
  .di-kicker-text a:hover { text-decoration: underline; }

  .di-h1 { font-family: var(--serif); font-size: clamp(34px, 4vw, 56px); font-weight: 900; line-height: 1.05; letter-spacing: -0.025em; }
  .di-h1 em { font-style: italic; color: var(--accent); }
  .di-h2 { font-family: var(--serif); font-size: clamp(24px, 2.6vw, 36px); font-weight: 700; line-height: 1.15; letter-spacing: -0.015em; }
  .di-h3 { font-family: var(--serif); font-size: 20px; font-weight: 700; line-height: 1.25; }
  .di-lede { font-size: 18px; font-weight: 300; line-height: 1.7; color: var(--ink-muted); border-left: 2px solid var(--rule); padding-left: 20px; }
  .di-text { font-size: 16px; line-height: 1.7; color: var(--ink-muted); }

  .di-hero { padding: 72px 0 48px; display: grid; grid-template-columns: 1fr 1fr; gap: 0 80px; align-items: end; }

  .di-btn { display: inline-block; background: var(--accent); color: white; font-size: 12px; font-weight: 500;
    letter-spacing: 0.12em; text-transform: uppercase; text-decoration: none; padding: 14px 28px; transition: opacity 0.15s; }
  .di-btn:hover { opacity: 0.85; }
  .di-btn-ghost { display: inline-block; color: var(--ink); font-size: 12px; font-weight: 500; letter-spacing: 0.12em;
    text-transform: uppercase; text-decoration: none; padding: 13px 26px; border: 1px solid var(--ink); }
  .di-btn-ghost:hover { border-color: var(--accent); color: var(--accent); }

  .di-rule { border: none; border-top: 1px solid var(--rule); margin: 0; }

  .di-list { list-style: none; display: flex; flex-direction: column; gap: 10px; }
  .di-list li { font-size: 15px; line-height: 1.55; color: var(--ink-muted); display: flex; gap: 10px; }
  .di-list li::before { content: '\\2014'; color: var(--accent); font-weight: 700; flex-shrink: 0; }

  .di-steps { list-style: none; counter-reset: step; display: flex; flex-direction: column; gap: 12px; }
  .di-steps li { counter-increment: step; font-size: 15px; line-height: 1.55; color: var(--ink-muted); display: flex; gap: 14px; }
  .di-steps li::before { content: counter(step, decimal-leading-zero); font-family: var(--serif); font-weight: 900; color: var(--accent); flex-shrink: 0; }

  .di-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 1px; background: var(--rule); border: 1px solid var(--rule); }
  .di-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1px; background: var(--rule); border: 1px solid var(--rule); }
  .di-cell { background: var(--paper); padding: 32px; }

  .di-card { background: var(--paper); padding: 28px 28px 24px; display: flex; flex-direction: column; gap: 10px;
    text-decoration: none; color: var(--ink); transition: background 0.15s; }
  .di-card:hover { background: var(--paper-warm); }
  .di-card-tag { font-size: 10px; font-weight: 500; letter-spacing: 0.14em; text-transform: uppercase; color: var(--accent); }
  .di-method-num { font-family: var(--serif); font-size: 40px; font-weight: 900; color: var(--ink); opacity: 0.08; line-height: 1; margin-bottom: 4px; }
  .di-card-more { font-size: 12px; letter-spacing: 0.1em; text-transform: uppercase; color: var(--accent); }

  .di-facts { display: grid; grid-template-columns: 160px 1fr; border: 1px solid var(--rule); }
  .di-facts dt, .di-facts dd { padding: 14px 18px; border-bottom: 1px solid var(--rule); font-size: 15px; line-height: 1.55; }
  .di-facts dt { background: var(--paper-warm); font-weight: 500; color: var(--ink); }
  .di-facts dd { color: var(--ink-muted); }
  .di-facts dt:last-of-type, .di-facts dd:last-of-type { border-bottom: none; }

  .di-section { padding: 56px 0; }

  .di-faq { border-top: 1px solid var(--rule); }
  .di-faq-item { padding: 22px 0; border-bottom: 1px solid var(--rule); display: grid; gap: 8px; max-width: 860px; }
  .di-faq-q { font-family: var(--serif); font-size: 18px; font-weight: 700; line-height: 1.3; color: var(--ink); }
  .di-footer { border-top: 1px solid var(--rule); background: var(--paper-warm); padding: 28px 32px; text-align: center; }
  .di-footer p { font-size: 12px; color: var(--ink-faint); }
  .di-footer a { color: var(--ink-muted); }

  @media (max-width: 960px) {
    .di-wrap { padding: 0 20px; }
    .di-hero, .di-grid-2, .di-grid-3 { grid-template-columns: 1fr; gap: 24px; }
    .di-grid-2, .di-grid-3 { gap: 1px; }
    .di-hero { padding: 48px 0 32px; }
    .di-facts { grid-template-columns: 1fr; }
    .di-facts dt { border-bottom: none; }
  }
`;
